import { getCompanyDetailsByUrl } from '@api/company';
import Login from '@components/Templates/Login';
import AuthenticationLayout from '@layouts/AuthenticationLayout';
import { LoadingBackdrop } from '@shared/molecules';
import { PublicCompanyDetails, castResponse } from 'custom.d';
import { Parser } from 'html-to-react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';

const LoginPage: React.FunctionComponent = () => {
  const { companyURL } = useParams<UrlParamsType>();
  const [companyDetails, setCompanyDetails] = useState<PublicCompanyDetails | null>(null);
  const [isCompanyConfigLoading, setIsCompanyConfigLoading] = useState(() => Boolean(companyURL));
  let content: JSX.Element | string | undefined;
  let organizationLogo: string | undefined;
  let organizationAltText: string | undefined;
  let organizationId = 0;
  let hideHapstarLogo = false;
  let hideSignUp = false;
  let platformName: string | null = null;

  // Domain-specific redirects for bare /login
  useEffect(() => {
    const { hostname, pathname } = window.location;

    if (hostname === 'wellbeing.flourish.co.uk' && pathname === '/login') {
      window.location.replace('https://app.click-learning.co.uk/login');

      return;
    }

    if (hostname === 'beacon.laurahydefoundation.org' && pathname === '/login') {
      window.location.replace('https://beacon.laurahydefoundation.org/login/beacon');
    }
  }, []);

  useEffect(() => {
    if (!companyURL) {
      setCompanyDetails(null);
      setIsCompanyConfigLoading(false);

      return undefined;
    }

    let cancelled = false;
    setIsCompanyConfigLoading(true);

    const getCompanyData = async () => {
      try {
        const response = await getCompanyDetailsByUrl(companyURL);
        if (cancelled) {
          return;
        }

        if (response?.success) {
          const data = castResponse<PublicCompanyDetails>(response);
          setCompanyDetails(data);
        } else {
          setCompanyDetails(null);
        }
      } catch {
        if (!cancelled) {
          setCompanyDetails(null);
        }
      } finally {
        if (!cancelled) {
          setIsCompanyConfigLoading(false);
        }
      }
    };

    getCompanyData();

    return () => {
      cancelled = true;
    };
  }, [companyURL]);

  if (isCompanyConfigLoading) {
    return (
      <AuthenticationLayout title="Login">
        <LoadingBackdrop text="Loading..." />
      </AuthenticationLayout>
    );
  }

  if (companyDetails !== null) {
    content = Parser().parse(companyDetails.loginText);
    organizationLogo = companyDetails.companyLogo
      ? `${process.env.PUBLIC_URL}/images/logos/${companyDetails.companyLogo}`
      : undefined;
    organizationAltText = `${companyDetails.companyName} logo`;
    organizationId = companyDetails.id;
    hideHapstarLogo = companyDetails.hideHapstarLogo;
    hideSignUp = companyDetails.hideSignUp === true;
    platformName =
      companyDetails.platformName &&
      companyDetails.platformName !== 'null' &&
      companyDetails.platformName !== null
        ? companyDetails.platformName
        : null;
  }

  return (
    <Login
      content={content}
      organizationLogo={organizationLogo}
      organizationAltText={organizationAltText}
      organizationId={organizationId}
      companyURL={companyURL}
      hideHapstarLogo={hideHapstarLogo}
      hideSignUp={hideSignUp}
      platformName={platformName}
    />
  );
};

type UrlParamsType = {
  companyURL: string;
};

export default LoginPage;
